import { Injectable } from '@angular/core';

import mixpanel from 'mixpanel-browser';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { AnalyticsAdapter, AnalyticsProps } from '@core/analytics-types';
import type { AnalyticsAPIMethods, AnalyticsClientOptions } from '@core/analytics-types';

const SUPER_PROPERTIES: string[] = [
  'Company ID',
  'Tellent App ID',
  'Tellent Org ID',
  'membership_id',
];

@Injectable()
export class AnalyticsMixpanelAdapter extends AnalyticsAdapter {
  public load(key: string, _options?: AnalyticsClientOptions): Observable<AnalyticsAPIMethods> {
    mixpanel.init(key, { track_pageview: false });

    const client: AnalyticsAPIMethods = {
      track: this.track.bind(this),
      identify: this.identify.bind(this),
      page: this.page.bind(this),
      alias: this.alias.bind(this),
      reset: this.reset.bind(this),
    };

    return of(client).pipe(shareReplay(1));
  }

  private track(eventName: string, eventProps?: AnalyticsProps, opts?: AnalyticsClientOptions) {
    if (this.shouldSendTo('Mixpanel', opts)) {
      mixpanel.track(eventName, eventProps);
    }
  }

  private identify(
    userId?: number,
    traits?: Record<string, string | number | boolean>,
    _opts?: AnalyticsClientOptions,
    callback?: () => void,
  ) {
    mixpanel.identify(`${userId}`);
    if (traits) {
      mixpanel.people.set(traits);
      const superProperties = SUPER_PROPERTIES.reduce(
        (acc, key) => ({ ...acc, [key]: traits[key] }),
        {},
      );
      mixpanel.register(superProperties);
    }

    if (callback !== undefined) {
      callback();
    }
  }

  private page(
    category?: string,
    name?: string,
    properties?: AnalyticsProps,
    opts?: AnalyticsClientOptions,
  ) {
    const pageName = [category, name].filter(part => part !== undefined).join(' ');
    const eventName = `Viewed ${pageName} Page`;
    const props = {
      category,
      name,
      ...properties,
      ...opts,
      path: window.location.pathname,
      search: window.location.search,
      title: document.title,
    };
    mixpanel.track(eventName, props);
  }

  private alias(id: string | number) {
    mixpanel.alias(`${id}`);
  }

  private reset() {
    mixpanel.reset();
  }
}
